'use strict';

/**
 * This item view can be used to provide a file upload
 *
 * @category   View
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var ColidoItemView = require('base/Colido.ItemView');

var template = require('../../templates/components/file-upload.hbs');

var FileUploadView = ColidoItemView.extend({
    template: template,

    imagesOnly: false,

    ui: {
        'component': '.input-file-upload',
        'fileInputName': '.input-file-upload-name',
        'fileInputWrapper': '.input-file-upload-select',
        'progressBar': '.input-file-upload-progress',
        'errorMessage': '.error-message',
        'hiddenFileInput': '.hidden-file-input',
        'actionUploadFile': '[data-action="upload-file"]',
    },

    events: {
        'click @ui.actionUploadFile': 'onActionUploadFile',
        'change @ui.hiddenFileInput': 'onFileSelected',
    },

    /**
     * States for this component
     * @type object
     */
    STATES: {
        PENDING: 'pendig',
        SELECTED: 'selected',
        UPLOADING: 'uploading',
        FINISHED: 'finished',
        ERROR: 'error',
    },

    /**
     * Selected file from the component
     * @type {null}|{object}
     */
    selectedFile: null,

    /**
     * Filreader object
     * @type {null}|{FileReader}
     */
    fileReader: null,

    /**
     * XHR Object
     * @type {null}|{XMLHttpRequest}
     */
    xhr: null,

    /**
     * API URL to use for upload
     * @type {string}
     */
    uploadHandler: null,

    initialize: function(options) {
        ColidoItemView.prototype.initialize.apply(this, arguments);

        // Set params
        this.uploadHandler = this.getOption('uploadHandler');

        // Set correct handler
        this.onFileLoaded = this.onFileLoaded.bind(this);
        this.onUploadProgress = this.onUploadProgress.bind(this);
        this.onUploadFinished = this.onUploadFinished.bind(this);
        this.onUploadError = this.onUploadError.bind(this);
        this.onUploadAbort = this.onUploadAbort.bind(this);
    },

    /**
     * Called, before view is destroyed.
     * We will destroy additional listener
     */
    onBeforeDestroy: function() {
        this._destroyListener();
    },

    setState: function(state, data) {
        // Set basic state and notify parents
        this.ui.component.attr('data-state', state);
        this.triggerMethod('upload:state:' + state, data);

        switch (state) {
            // File selected
            case this.STATES.SELECTED:
                this.ui.fileInputName.val(this.selectedFile.name);
                this.ui.fileInputWrapper.removeClass('error');
                this.ui.errorMessage.html('');
                break;

            // File uploading
            case this.STATES.UPLOADING:
                this.ui.actionUploadFile.prop('disabled', true).addClass('is-loading');
                this.ui.fileInputWrapper.removeClass('error');
                this.ui.errorMessage.html('');
                break;

            // File finished
            case this.STATES.FINISHED:
                this.ui.actionUploadFile.prop('disabled', false).removeClass('is-loading');
                this.ui.hiddenFileInput.val('');
                this.ui.progressBar.css({width: '0'});
                this.ui.fileInputWrapper.removeClass('error');
                this.ui.errorMessage.html('');
                break;

            // File finished
            case this.STATES.ERROR:
                this.ui.actionUploadFile.prop('disabled', false).removeClass('is-loading');
                this.ui.hiddenFileInput.val('');
                this.ui.progressBar.css({width: '0'});
                this.ui.fileInputWrapper.addClass('error');
                this.ui.errorMessage.html(this.getErrorMessage(data.status, data));
                break;
        }
    },

    /**
     * Is triggered, when a file has been selected
     * @param {object} event The change event with the files
     */
    onFileSelected: function(event) {
        if (event.currentTarget.files.length) {
            this.selectedFile = _.first(event.currentTarget.files);
            this.setState(this.STATES.SELECTED, event);
        }
    },

    /**
     * Action, when upload is pressed
     * We will upload the files
     */
    onActionUploadFile: function() {
        this._processFile();
        this.setState(this.STATES.UPLOADING);
    },

    /**
     * Is triggered, when the file is loaded and the upload can be started
     * @param {ProgressEvent} event The event of the file load
     */
    onFileLoaded: function(event) {
        this._processUpload(event.target.file, event.target.index);
    },

    /**
     * Is triggered on upload progress
     * @param {XMLHttpRequestProgressEvent} event
     */
    onUploadProgress: function(event) {
        var percentCompleted = 0;
        if (event.lengthComputable) {
            percentCompleted = (event.loaded / event.total) * 100;
        }

        this.ui.progressBar.css({width: percentCompleted + '%'});
    },

    /**
     * Triggered when download is finished
     * @param {XMLHttpRequestProgressEvent} event
     */
    onUploadFinished: function(event) {
        this.setState(this.STATES.FINISHED, event);
        this._destroyListener();
    },

    /**
     * Triggered when upload failed
     * @param {XMLHttpRequestProgressEvent} event
     */
    onUploadError: function(event) {
        this.setState(this.STATES.ERROR, event);
        this._destroyListener();
    },

    /**
     * Triggered, when upload was aborted
     * @param {XMLHttpRequestProgressEvent} event
     */
    onUploadAbort: function(event) {
        this.setState(this.STATES.ERROR, event);
        this._destroyListener();
    },

    /**
     * Processes a file, by creating a FileReader
     * @private
     */
    _processFile: function() {
        this.fileReader = new FileReader();
        this.fileReader.index = 0;
        this.fileReader.file = this.selectedFile;
        this.fileReader.addEventListener('load', this.onFileLoaded, true);
        this.fileReader.readAsDataURL(this.selectedFile);
    },

    /**
     * Processes the upload, when file was loaded
     * @param {File}   file  The file to upload
     * @param {number} index The index of the file
     * @see onFileLoaded
     * @private
     */
    _processUpload: function(file, index) {
        var fd = new FormData();
        fd.append('file', file);
        fd.append('fileIndex', index ? index : 0);

        // Processing XHR
        this.xhr = $.ajax({
            xhr: function() {
                var xhr = new window.XMLHttpRequest();
                xhr.upload.addEventListener('progress', this.onUploadProgress, true);

                return xhr;
            }.bind(this),
            method: 'POST',
            data: fd,
            contentType: false,
            processData: false,
            url: this.uploadHandler,
            error: this.onUploadError,
            success: this.onUploadFinished,
        });
    },

    /**
     * Destroys additional event listener
     * @private
     */
    _destroyListener: function() {
        if (this.fileReader) {
            this.fileReader.removeEventListener('load', this.onFileLoaded, true);
            delete this.fileReader;
        }
    },

    /**
     * Returns a localised user-facing error message.
     * @param {number} statusCode HTTP status code returned by server
     * @param {object} data       The data object from the server
     */
    getErrorMessage: function (statusCode, data) {
        var message = App.t('base::file-upload:unknown-error');
        if (!_.isUndefined(data.responseJSON)) {
            message = App.t('base::file-upload:error:' + statusCode);

            if (statusCode !== 500 && data.responseJSON.message) {
                message = data.responseJSON.message;
            }
        }

        return message;
    },
});

module.exports = FileUploadView;